import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import ImpactForecast from './ImpactForecast';
import { IndicatorSectionKey } from '../../../../ReportBuilder/Components/ImpactIndicator/utils';
import { ModelSaveWarning } from './modelErrors';
import { HandleDeleteIndicatorData } from './ImpactTableStage';
import {
  IndicatorDetails,
  sectionKeyToString,
} from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import {
  getForecast,
  stripCommas,
  getCrossSectionForecastMatches,
  CROSS_SECTION_FORECAST_CHECKS,
  isForecastResultOneMatch,
} from './forecastValidationUtils';
import { useSaveDetailProperties } from 'shared/providers/IndicatorProvider/hooks';
import { GetSaveStatus } from '../../../../ReportBuilder/Components/ImpactIndicator/IndicatorTextarea';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useReport } from 'shared/providers/ReportProvider';
import { mergeKeyNames } from 'shared/utils/mergeClassNames';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import ForecastMatchWarningModal from './ForecastMatchWarningModal';
import ForecastResultOneWarningModal from './ForecastResultOneWarningModal';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';

interface ImpactForecastWrapperProps {
  reportId: number;
  isReportPassed: boolean;
  isForecastError: boolean;
  isForecastWarning: boolean;
  isFinalForecastError: boolean;
  isFinalForecastWarning: boolean;
  sectionKey: IndicatorSectionKey;
  indicatorById: {
    index: number;
    indicator: IndicatorDetails;
  };
  indicator: HandleDeleteIndicatorData;
  onEditChange: (warning: ModelSaveWarning, isEditing: boolean) => unknown;
}

const timeout: number | undefined = undefined;
const ImpactForecastWrapper: React.FC<ImpactForecastWrapperProps> = ({
  reportId,
  isReportPassed,
  sectionKey,
  indicator,
  indicatorById,
  isForecastError,
  isForecastWarning,
  isFinalForecastError,
  isFinalForecastWarning,
  onEditChange,
}) => {
  const { forecastField, finalForecastField, isInterimStage } = useReport();
  const i = indicatorById.indicator;
  const forecastFieldProps = forecastField(i);
  const finalForecastFieldProps = finalForecastField(i);
  const { impactDetails } = useIndicator();
  const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
  const [saveDetailProperties, saveRequestState] = useSaveDetailProperties();
  const [forecast, setForecast] = useState<string>(getForecast(false, i));
  const [finalForecast, setFinalForecast] = useState<string>(
    getForecast(true, i),
  );
  const [warningModal, setWarningModal] = useState<{
    open: boolean;
    forecastValue: string;
    finalForecastValue: string;
    initialNote: string;
    forecastMatches: IndicatorDetails[];
    finalForecastMatches: IndicatorDetails[];
    sourceSectionLabel: string;
    targetSectionLabel: string;
  }>({
    open: false,
    forecastValue: '',
    finalForecastValue: '',
    initialNote: '',
    forecastMatches: [],
    finalForecastMatches: [],
    sourceSectionLabel: '',
    targetSectionLabel: '',
  });

  const isUnsaved =
    forecast !== getForecast(false, i) ||
    finalForecast !== getForecast(true, i);

  // Re-sync local state with the canonical (comma-formatted) values from the
  // indicator whenever they change — either because we switched to a different
  // indicator, or because a save just merged new values into impactDetails.
  // This is what flips `isUnsaved` back to false after a successful save.
  useEffect(() => {
    setForecast(getForecast(false, i));
    setFinalForecast(getForecast(true, i));
  }, [indicator.indicatorId, i.forecast, i.finalForecast]);

  useEffect(() => {
    onEditChange(
      {
        propertyName: isReportPassed ? 'finalForecast' : 'forecast',
        sectionKey: indicator.sectionKey,
        index: indicator.impactDetailIndex,
        indicationId: indicator.indicatorId,
      },
      isUnsaved,
    );
  }, [isUnsaved]);

  const [resultOneModal, setResultOneModal] = useState<{
    open: boolean;
    forecastValue: string;
    finalForecastValue: string;
    initialNote: string;
  }>({
    open: false,
    forecastValue: '',
    finalForecastValue: '',
    initialNote: '',
  });

  const persistForecast = useCallback(
    (
      forecastValue: string,
      finalForecastValue: string,
      level1Forecast?: string,
      level1ForecastResultOne?: string,
    ) => {
      saveDetailProperties(indicator.indicatorId, sectionKey, {
        forecast: forecastFieldProps.hide ? null : forecastValue,
        finalForecast: finalForecastFieldProps.hide ? null : finalForecastValue,
        ...(level1Forecast !== undefined ? { level1Forecast } : {}),
        ...(level1ForecastResultOne !== undefined
          ? { level1ForecastResultOne }
          : {}),
      });
    },
    [
      indicator.indicatorId,
      sectionKey,
      forecastFieldProps.hide,
      finalForecastFieldProps.hide,
      saveDetailProperties,
    ],
  );

  const handleSave = useCallback(() => {
    window.clearTimeout(timeout);
    const forecastValue = stripCommas(forecast);
    const finalForecastValue = stripCommas(finalForecast);

    // When saving a reach/capacity development forecast, warn if the value
    // matches any forecast/finalForecast in the opposite section.
    const targetSectionKey = CROSS_SECTION_FORECAST_CHECKS[sectionKey];
    if (levelOneFeedbackEnabled && targetSectionKey && impactDetails) {
      const {
        forecastMatches,
        finalForecastMatches,
      } = getCrossSectionForecastMatches({
        indicator: i,
        targetIndicators: impactDetails[targetSectionKey] || [],
        forecastValue,
        finalForecastValue,
        hideForecast: forecastFieldProps.hide,
        hideFinalForecast: finalForecastFieldProps.hide,
      });

      if (forecastMatches.length || finalForecastMatches.length) {
        setWarningModal({
          open: true,
          forecastValue,
          finalForecastValue,
          initialNote: i.level1Forecast || '',
          forecastMatches,
          finalForecastMatches,
          sourceSectionLabel: sectionKeyToString(sectionKey),
          targetSectionLabel: sectionKeyToString(targetSectionKey),
        });
        return;
      }
    }

    if (
      levelOneFeedbackEnabled &&
      isForecastResultOneMatch(sectionKey, forecastValue, finalForecastValue)
    ) {
      setResultOneModal({
        open: true,
        forecastValue,
        finalForecastValue,
        initialNote: i.level1ForecastResultOne || '',
      });
      return;
    }

    persistForecast(forecastValue, finalForecastValue);
  }, [
    i,
    forecast,
    finalForecast,
    forecastFieldProps,
    finalForecastFieldProps,
    sectionKey,
    impactDetails,
    persistForecast,
    levelOneFeedbackEnabled,
  ]);

  const handleConfirmWarning = useCallback(
    (note: string) => {
      const { forecastValue, finalForecastValue } = warningModal;
      setWarningModal(s => ({ ...s, open: false }));
      persistForecast(forecastValue, finalForecastValue, note);
    },
    [warningModal, persistForecast],
  );

  const handleCloseWarning = useCallback(() => {
    setWarningModal(s => ({ ...s, open: false }));
  }, []);

  const handleConfirmResultOne = useCallback(
    (note: string) => {
      const { forecastValue, finalForecastValue } = resultOneModal;
      setResultOneModal(s => ({ ...s, open: false }));
      persistForecast(forecastValue, finalForecastValue, undefined, note);
    },
    [resultOneModal, persistForecast],
  );

  const handleCloseResultOne = useCallback(() => {
    setResultOneModal(s => ({ ...s, open: false }));
  }, []);

  if (forecastFieldProps.hide && finalForecastFieldProps.hide) {
    return null;
  }

  return (
    <>
      {!forecastFieldProps.hide && (
        <>
          <TrueImpactText
            variant="heading-label"
            color="blackBase"
            className="forecast-text"
          >
            {forecastFieldProps.label}
          </TrueImpactText>
          <ImpactForecast
            key={mergeKeyNames(
              'initial-forecast',
              sectionKey,
              indicator.indicatorId,
            )}
            reportId={reportId}
            placeholder={forecastFieldProps.placeholder}
            isFinalForecast={false}
            forecast={forecast}
            setForecast={setForecast}
            disabled={forecastFieldProps.disabled}
            isWarning={isForecastWarning}
            isError={
              isForecastError || saveRequestState.type === 'REQUEST_ERROR'
            }
          />
        </>
      )}
      {isReportPassed && !finalForecastFieldProps.hide && (
        <>
          <TrueImpactText
            variant="heading-label"
            color="blackBase"
            className="forecast-text final-forecast-text"
          >
            {finalForecastFieldProps.label}
          </TrueImpactText>
          <ImpactForecast
            key={mergeKeyNames(
              'final-forecast',
              sectionKey,
              indicator.indicatorId,
            )}
            reportId={reportId}
            isFinalForecast={true}
            forecast={finalForecast}
            placeholder={finalForecastFieldProps.placeholder}
            setForecast={setFinalForecast}
            isWarning={isFinalForecastWarning}
            isError={
              isFinalForecastError || saveRequestState.type === 'REQUEST_ERROR'
            }
          />
        </>
      )}
      {isUnsaved && (
        <TrueImpactButton
          style={{ marginTop: SPACERS.tiny }}
          onClick={handleSave}
          variant="secondary-small"
          className="save-button"
          disabled={saveRequestState.type === 'REQUEST_START'}
        >
          <FontAwesomeIcon icon={faSave} />
          Save
        </TrueImpactButton>
      )}
      <GetSaveStatus state={saveRequestState} />
      <ForecastResultOneWarningModal
        key={
          resultOneModal.open ? resultOneModal.initialNote : 'result-one-closed'
        }
        open={resultOneModal.open}
        onClose={handleCloseResultOne}
        onConfirm={handleConfirmResultOne}
        initialNote={
          resultOneModal.initialNote ||
          indicator.impactDetailIndicator.level1ForecastResultOne ||
          ''
        }
        forecastIsOne={resultOneModal.forecastValue === '1'}
        finalForecastIsOne={resultOneModal.finalForecastValue === '1'}
        isInterimStage={isInterimStage}
        indicatorName={i.indicatorName}
      />
      <ForecastMatchWarningModal
        key={warningModal.open ? warningModal.initialNote : 'closed'}
        open={warningModal.open}
        onClose={handleCloseWarning}
        onConfirm={handleConfirmWarning}
        initialNote={
          warningModal.initialNote ||
          indicator.impactDetailIndicator.level1Forecast ||
          ''
        }
        forecastMatches={warningModal.forecastMatches}
        finalForecastMatches={warningModal.finalForecastMatches}
        sourceSectionLabel={warningModal.sourceSectionLabel}
        targetSectionLabel={warningModal.targetSectionLabel}
        isInterimStage={isInterimStage}
      />
    </>
  );
};

export default ImpactForecastWrapper;
